import { RRule, rrulestr } from 'rrule'

import { Format } from 'services/Dates'
import { store } from 'store/reducers'
import { t, currentLang } from 'translations'

const getUTCdate = date => {
  const formattedDate = Format(date, { inFormat: 'date', outFormat: 'date' })
  const [year, month, day] = formattedDate.split('-').map(string => parseInt(string))
  // Months are 0 indexed in javascript
  return new Date(Date.UTC(year, month - 1, day, 23, 59))
}

/**
 * Converts the schedule form rrule to a version compatible with the rrule library
 * @param {*} rrule Rrule object from schedule form
 * @returns rrule object from the rrule library
 */
export const convertToRuleObject = rrule => {
  if (rrule.frequency === 'NONE') return

  // Get the first day of the week to specify it in the ruleConfig
  const {
    settings: { defaultBeginningOfWeekDay }
  } = store.getState()

  const dayMap = {
    0: RRule.SU,
    1: RRule.MO,
    2: RRule.TU,
    3: RRule.WE,
    4: RRule.TH,
    5: RRule.FR,
    6: RRule.SA
  }

  // All rules have an interval
  let ruleConfig = {
    interval: rrule.interval || 1,
    dtstart: getUTCdate(rrule.dtstart || new Date()),
    wkst: dayMap[defaultBeginningOfWeekDay]
  }

  // Handle how the rrule ends
  if (rrule.ends === 'COUNT') ruleConfig.count = rrule.count
  else ruleConfig.until = getUTCdate(rrule.until ? rrule.until : new Date())

  // Handle the frequency, only weekly and daily are supported for now
  if (rrule.frequency === 'WEEKLY') {
    ruleConfig.freq = RRule.WEEKLY
    // Add constants for days of week depending on form checkboxes
    ruleConfig.byweekday = [
      rrule.byWeekDay.monday ? RRule.MO : null,
      rrule.byWeekDay.tuesday ? RRule.TU : null,
      rrule.byWeekDay.wednesday ? RRule.WE : null,
      rrule.byWeekDay.thursday ? RRule.TH : null,
      rrule.byWeekDay.friday ? RRule.FR : null,
      rrule.byWeekDay.saturday ? RRule.SA : null,
      rrule.byWeekDay.sunday ? RRule.SU : null
    ].filter(Boolean)
  } else if (rrule.frequency === 'DAILY') {
    ruleConfig.freq = RRule.DAILY
  } else return

  return new RRule(ruleConfig)
}

export const convertFromRuleString = (rruleString = '', { defaultUntil } = {}) => {
  // Default values for the form
  let rrule = {
    frequency: 'NONE',
    count: 1,
    until: defaultUntil || new Date(),
    interval: 1,
    ends: 'UNTIL',
    byWeekDay: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  }

  if (!rruleString) return rrule

  const { origOptions: ruleConfig } = rrulestr(rruleString)

  if (typeof ruleConfig.interval === 'number') rrule.interval = ruleConfig.interval
  if ([2, 3].includes(ruleConfig.freq)) rrule.frequency = { 2: 'WEEKLY', 3: 'DAILY' }[ruleConfig.freq]
  if (ruleConfig.until) {
    rrule.until = ruleConfig.until
  } else if (typeof ruleConfig.count === 'number') {
    rrule.count = ruleConfig.count
    rrule.ends = 'COUNT'
  }

  if (typeof ruleConfig.byweekday !== 'undefined') {
    for (const weekday of ruleConfig.byweekday) {
      rrule.byWeekDay[
        {
          MO: 'monday',
          TU: 'tuesday',
          WE: 'wednesday',
          TH: 'thursday',
          FR: 'friday',
          SA: 'saturday',
          SU: 'sunday'
        }[weekday.toString()]
      ] = true
    }
  }

  return rrule
}

/**
 * Extends the RRule object to support our translations
 * @returns translated representation of the current rul
 */
RRule.prototype.toTextTranslated = function () {
  let langMap = {
    fr: {
      dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      monthNames: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
    },
    en: {
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
  }

  return this.toText(token => t(`components.recurrenceRule.toText.${token.toUpperCase()}`), langMap[currentLang()])
}
